import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Rocket, Zap, Globe, Cpu, ChevronRight, ArrowRight, ArrowUpRight, 
  Menu, X, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, GraduationCap, ChevronLeft, 
  ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera, Shield
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const FutureScholar: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Rocket;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Rocket;
  };

  const schoolName = previewData?.name || "Nebula Academy";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2070&auto=format&fit=crop"],
      tagline: "The Future of Learning",
      titlePrefix: "Unlock Potential",
      subtitle: "Join the next generation of visionaries in a space where advanced curriculum meets cutting-edge technology and human curiosity."
    },
    features: [
      { icon: "Cpu", title: "AI-Integrated", desc: "Adaptive learning pathways powered by proprietary AI models." },
      { icon: "Zap", title: "Quantum Logic", desc: "Primary focus on computational thinking and rapid prototyping." },
      { icon: "Globe", title: "Virtual Campus", desc: "Collaborate with global laboratories through VR/AR interfaces." }
    ],
    programs: [
      { title: "Kinetics Lab", age: "Years 1-8", desc: "Foundational robotics and biomechanics for young explorers.", img: "https://images.unsplash.com/photo-1531297484001-80022131f5a1?q=80&w=2020&auto=format&fit=crop" },
      { title: "System Architect", age: "Years 9-13", desc: "Full-stack engineering and decentralized finance mastery.", img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop" }
    ],
    stats: [
        { value: "0ms", label: "LATENCY IN LEARNING" },
        { value: "50+", label: "ACTIVE PATENTS" },
        { value: "2040", label: "HORIZON_FOCUS" }
    ],
    news: [
        { title: "Mars Habitation Win", date: "MAY 05", desc: "Our students took first place in the NASA Mars colony design contest.", img: "https://images.unsplash.com/photo-1614728894747-a83421e2b9c9?q=80&w=2067&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'NODES', href: '#features' },
        { label: 'PATHWAYS', href: '#programs' },
        { label: 'VISUALS', href: '#gallery' },
        { label: 'TERMINAL', href: '#contact' }
    ],
    team: [
        { name: "Dr. Zephyr Thorne", role: "Logic Director", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=1974&auto=format&fit=crop" },
        { name: "Nova Sterling", role: "Interface Lead", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1961&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Hardware", img: "https://images.unsplash.com/photo-1517420704952-d9f39e95b43e?q=80&w=2070&auto=format&fit=crop", title: "Fabrication Floor" },
        { category: "Software", img: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop", title: "Code Sanctuary" }
    ],
    facilities: [
        { title: "Holographic Suite", desc: "3D recursive simulation theater for complex data visuals.", img: "https://images.unsplash.com/photo-1478416272538-5f7e51dc5400?q=80&w=2070&auto=format&fit=crop", icon: "Layout" },
        { title: "Clean Room", desc: "Class-100 microchip assembly and research environment.", img: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?q=80&w=2070&auto=format&fit=crop", icon: "Shield" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "BEYOND THE HORIZON.",
          description: previewData?.about?.description || schoolName + " is a high-acceleration environment where we discard traditional boundaries between disciplines. We are building the architects of the 22nd century.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Nebula_Manifesto.v3", size: "1.2 MB", type: "DATA" },
          { title: "Protocol_Guide.sys", size: "4.5 MB", type: "EXE" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "NODE_01, GALAXY HUB, NAIROBI",
        phone: previewData?.contact?.phone || "+101 010 1010",
        email: previewData?.contact?.email || "stream@nebula.ac",
        officeHours: previewData?.contact?.officeHours || "24/7 ASYNC // 0900 - 1500 SYNC",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "System Manifest",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Digital Repositories",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#06b6d4');
        root.style.setProperty('--color-secondary', colors.secondary || '#a855f7');
        root.style.setProperty('--color-accent', colors.accent || '#3b82f6');
        root.style.setProperty('--color-background', colors.background || '#0f172a');
        root.style.setProperty('--color-text', colors.text || '#f8fafc');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Plus Jakarta Sans'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=Inter:wght@300;400;600&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Defragment item?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="future-scholar bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-black overflow-x-hidden">
      
      {/* HUD Header */}
      <header className="fixed w-full z-[100] bg-[var(--color-background)]/60 backdrop-blur-xl border-b border-white/5 h-24">
          <div className="container mx-auto px-6 h-full flex justify-between items-center">
              <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-xl flex items-center justify-center p-0.5">
                      <div className="w-full h-full bg-[var(--color-background)] rounded-[10px] flex items-center justify-center">
                          <Rocket size={24} className="text-[var(--color-primary)] animate-pulse" />
                      </div>
                  </div>
                  <span className="text-2xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] uppercase">
                      <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                  </span>
              </div>
              
              <div className="hidden lg:flex items-center gap-8">
                  <nav className="flex gap-10 text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
                      {mockData.nav.filter(n => n.visible).map((n, i) => (
                          <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-2">
                             <div className="w-1 h-1 bg-[var(--color-primary)] opacity-0 group-hover:opacity-100" />
                             {n.label}
                          </a>
                      ))}
                  </nav>
                  <Button className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/80 text-black font-extrabold rounded-full px-8 h-12 text-[10px] uppercase tracking-widest shadow-[0_0_30px_rgba(6,182,212,0.3)]">
                      Portal
                  </Button>
              </div>

              <button className="lg:hidden text-white" onClick={() => setMenuOpen(!menuOpen)}>
                  {menuOpen ? <X size={32} /> : <Menu size={32} />}
              </button>
          </div>
      </header>

      {/* Hero Section - The Synthesis */}
      {mockData.sectionVisibility.hero && (
      <section className="relative pt-64 pb-32 overflow-hidden">
          {/* Animated Background Gradients */}
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[var(--color-primary)]/10 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[var(--color-secondary)]/10 rounded-full blur-[120px]" />
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20 pointer-events-none" />

          <div className="container mx-auto px-6 relative z-10 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold uppercase tracking-[0.4em] mb-12 animate-in slide-in-from-bottom-8 duration-700">
                  <Zap size={14} className="text-[var(--color-primary)]" />
                  <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
              </div>
              <h1 className="text-6xl md:text-9xl font-black italic tracking-tighter leading-[0.8] mb-12 uppercase drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
                  <span className="text-white block">
                    <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} />
                  </span>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[var(--color-primary)] via-[var(--color-secondary)] to-[var(--color-primary)] bg-[length:200%_auto] animate-gradient-flow">BEYOND REACH.</span>
              </h1>
              <p className="text-lg md:text-xl font-light text-white/50 max-w-3xl mx-auto mb-16 leading-relaxed">
                  <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center gap-6 mb-32">
                  <Button className="h-16 px-12 bg-[var(--color-primary)] text-black rounded-full font-black uppercase tracking-widest text-xs shadow-[0_0_50px_rgba(6,182,212,0.4)] hover:scale-105 transition-all">
                      Commence Training
                  </Button>
                  <Button variant="outline" className="h-16 px-12 border-white/10 text-white rounded-full font-black uppercase tracking-widest text-xs hover:bg-white/5 transition-all">
                      Scan Campus <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
              </div>

              <div className="max-w-6xl mx-auto relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] rounded-3xl blur opacity-20 group-hover:opacity-40 transition-all duration-1000" />
                  <div className="relative bg-[var(--color-background)] rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
                       <VisualImageEditable 
                          src={mockData.hero.backgroundImage} 
                          className="w-full aspect-video md:aspect-[21/9] object-cover hover:scale-105 transition-transform duration-[3s]" 
                          alt="Main Visual"
                          onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                          isEditMode={!!isEditMode}
                      />
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Grid Features - Nodes */}
      {mockData.sectionVisibility.features && (
      <section id="features" className="py-40 border-y border-white/5 relative">
          <div className="container mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-1px bg-white/5 border border-white/5">
                  {mockData.features.map((item, i) => (
                      <div key={i} className="bg-[var(--color-background)] p-12 group hover:bg-white/[0.02] transition-all relative overflow-hidden">
                          <div className="absolute top-0 right-0 p-8 text-8xl font-black text-white/[0.02] uppercase pointer-events-none">{i+1}</div>
                          <div className="w-16 h-16 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-2xl flex items-center justify-center mb-10 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
                              {React.createElement(item.icon, { size: 32, className: "text-white" })}
                          </div>
                          <h3 className="text-2xl font-black uppercase tracking-tight mb-6 group-hover:text-[var(--color-primary)] transition-colors">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-white/40 leading-relaxed font-light mb-10 group-hover:text-white/60 transition-colors">
                            <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <a href="#" className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-[var(--color-primary)] opacity-0 group-hover:opacity-100 transition-all">
                              ACCESS_PROTOCOL <ArrowRight size={14}/>
                          </a>
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="absolute bottom-4 right-4 text-[9px] font-bold text-red-400 opacity-20 hover:opacity-100">Drop</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('features', { title: "NEW NODE", desc: "Protocol data description.", icon: "Cpu" })} className="p-12 border-4 border-dashed border-white/5 flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-all">
                          <Plus size={48} className="text-white/20"/>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Program Tracks - Pathways */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40">
          <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-24 gap-8">
                  <div className="max-w-2xl">
                      <span className="text-[var(--color-secondary)] font-black tracking-[0.5em] uppercase text-xs mb-4 block">SYNAPTIC PATHWAYS</span>
                      <h2 className="text-6xl md:text-8xl font-black italic uppercase leading-none tracking-tighter">ENGINEER <br/> <span className="text-white/20 group-hover:text-white transition-colors">THE UNKNOWN.</span></h2>
                  </div>
                  <p className="text-white/40 max-w-sm font-light text-right italic">Our curriculum is non-linear, allowing students to bridge between quantum mechanics and creative expression.</p>
              </div>

              <div className="space-y-6">
                  {mockData.programs.map((p, i) => (
                      <div key={i} className="group relative h-[400px] md:h-[500px] rounded-3xl overflow-hidden border border-white/10 cursor-pointer">
                          <VisualImageEditable 
                              src={p.img} alt={p.title}
                              className="absolute inset-0 w-full h-full object-cover grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-70 group-hover:scale-105 transition-all duration-1000"
                              onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                              isEditMode={!!isEditMode}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-background)] via-transparent to-transparent" />
                          <div className="absolute inset-0 p-12 flex flex-col justify-end">
                              <div className="flex flex-col md:flex-row justify-between items-end gap-8">
                                  <div className="max-w-xl">
                                      <div className="inline-block px-4 py-1 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold text-[var(--color-primary)] uppercase tracking-widest mb-6">DEP // {p.age}</div>
                                      <h3 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6 italic leading-none">
                                        <VisualTextEditable value={p.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                                      </h3>
                                      <p className="text-white/60 font-medium leading-relaxed max-w-md hidden md:block">
                                        <VisualTextEditable value={p.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                                      </p>
                                  </div>
                                  <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 group-hover:bg-[var(--color-primary)] group-hover:text-black transition-all">
                                      <ArrowUpRight size={32} />
                                  </div>
                              </div>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="absolute top-6 right-6 z-20 text-[10px] font-black uppercase text-red-400 bg-black/50 px-4 py-2 rounded-full border border-red-400/30">Eject Pathway</button>}
                      </div>
                  ))}
                  {isEditMode && (
                       <div onClick={() => addArrayItem('programs', { title: "NEW ARCHITECT", age: "GEN-Z", desc: "Next gen meta-physics description." })} className="h-40 border-4 border-dashed border-white/5 rounded-3xl flex items-center justify-center cursor-pointer hover:bg-white/5 transition-all group">
                           <Plus size={32} className="text-white/20 group-hover:text-[var(--color-primary)]" />
                       </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Stats - Telemetry */}
      {mockData.sectionVisibility.stats && (
      <section className="py-32 container mx-auto px-6">
          <div className="bg-white/[0.02] border border-white/5 rounded-[40px] p-16 md:p-24 grid grid-cols-1 md:grid-cols-3 gap-20">
              {mockData.stats.map((s, i) => (
                  <div key={i} className="text-center group">
                      <div className="text-6xl md:text-8xl font-black bg-clip-text text-transparent bg-gradient-to-b from-white to-white/10 mb-4 tracking-tighter font-[var(--font-heading)]">
                        <VisualTextEditable value={s.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-[0.5em] text-[var(--color-primary)] opacity-40 group-hover:opacity-100 transition-opacity">
                        <VisualTextEditable value={s.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* Facilities - The Modules */}
      {mockData.sectionVisibility.facilities && (
      <section id="facilities" className="py-40">
           <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/5 border border-white/5">
                {mockData.facilities.map((fac, i) => (
                    <div key={i} className="bg-[var(--color-background)] p-12 md:p-20 relative group overflow-hidden">
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity">
                             <VisualImageEditable 
                                src={fac.img} alt={fac.title} 
                                className="w-full h-full object-cover" 
                                onImageChange={(u) => handleUpdate(`facilities[${i}].img`, u)} 
                                isEditMode={!!isEditMode}
                            />
                        </div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-6 mb-10">
                                <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10">
                                    {React.createElement(fac.icon, { size: 32, className: "text-[var(--color-secondary)]" })}
                                </div>
                                <h3 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter">
                                    <VisualTextEditable value={fac.title} onSave={(v) => handleUpdate(`facilities[${i}].title`, v)} isEditMode={!!isEditMode} />
                                </h3>
                            </div>
                            <p className="text-lg font-light text-white/40 leading-relaxed max-w-md italic mb-10">
                                <VisualTextEditable value={fac.desc} onSave={(v) => handleUpdate(`facilities[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                            </p>
                            <div className="flex gap-2">
                                {[1,2,3].map(dot => <div key={dot} className="w-8 h-1 bg-[var(--color-secondary)] opacity-20 group-hover:opacity-100 transition-opacity" />)}
                            </div>
                        </div>
                        {isEditMode && <button onClick={() => removeArrayItem('facilities', i)} className="absolute bottom-4 right-4 text-[9px] font-bold text-red-400 opacity-20 hover:opacity-100">Drop Facility</button>}
                    </div>
                ))}
           </div>
      </section>
      )}

      {/* News - Data Packets */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 bg-white/5">
          <div className="container mx-auto px-6">
              <div className="flex justify-between items-center mb-24 border-b border-white/10 pb-12">
                   <h2 className="text-5xl md:text-8xl font-black italic tracking-tighter uppercase">ARCHIVE.</h2>
                   <div className="hidden md:flex gap-4">
                       {[0,1,2].map(n => <div key={n} className="w-2 h-2 rounded-full bg-[var(--color-primary)]" />)}
                   </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-1px bg-white/5">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="bg-[var(--color-background)] p-12 group hover:bg-white/[0.04] transition-all">
                          <div className="flex justify-between text-[10px] font-bold text-white/20 uppercase tracking-[0.3em] mb-10">
                              <span>TRANS // {item.date}</span>
                              <span className="text-[var(--color-primary)]">READY</span>
                          </div>
                          <h3 className="text-3xl font-black uppercase leading-none tracking-tighter mb-8 group-hover:text-[var(--color-primary)] transition-colors italic">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <div className="aspect-[4/3] overflow-hidden mb-10 border border-white/10">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                          </div>
                          <p className="text-white/40 font-light leading-relaxed mb-10 line-clamp-3 group-hover:text-white/60">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <Button variant="ghost" className="p-0 text-[var(--color-primary)] hover:bg-transparent flex items-center gap-4 text-[10px] font-black uppercase tracking-widest group-hover:gap-6 transition-all">
                            SYNC_STREAM <Send size={14} />
                          </Button>
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads / Resource Uplink */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-24 bg-white/[0.02] border-y border-white/5 px-6">
          <div className="container mx-auto">
              <div className="flex items-center gap-8 mb-20">
                   <h2 className="text-xs font-black uppercase tracking-[0.8em] text-[var(--color-primary)] opacity-40">
                       <VisualTextEditable 
                           value={mockData.labels.downloadsSection} 
                           onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                           isEditMode={!!isEditMode} 
                           as="span" 
                       />
                   </h2>
                   <div className="h-px flex-1 bg-gradient-to-r from-[var(--color-primary)]/20 to-transparent" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="bg-[var(--color-background)] p-10 group border border-white/5 hover:border-[var(--color-primary)]/50 transition-all shadow-2xl relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-[var(--color-primary)] opacity-0 group-hover:opacity-10 blur-[40px] transition-opacity" />
                          <FileText size={48} className="text-white/10 mb-8 group-hover:text-[var(--color-primary)] transition-colors" />
                          <h4 className="text-sm font-black uppercase tracking-widest text-white mb-3 truncate leading-none">
                            <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h4>
                          <p className="text-[10px] font-bold text-white/20 uppercase tracking-[0.2em] flex items-center gap-4 italic text-wrap">
                               <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" /> // 
                               <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" />
                          </p>
                          <div className="mt-10 flex justify-between items-center border-t border-white/5 pt-8">
                               <button className="text-[10px] font-black text-[var(--color-primary)] uppercase tracking-[0.4em] hover:brightness-150 transition-all">SYNC_DATA()</button>
                               <Download size={20} className="text-white/10 group-hover:text-[var(--color-primary)]" />
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="absolute top-4 right-4 text-[9px] font-black text-red-500 opacity-20 hover:opacity-100 uppercase">Delete</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('downloads', { title: "NEW_NODE.dat", size: "1.0 GB", type: "RAW" })} className="p-10 border-4 border-dashed border-white/5 flex items-center justify-center cursor-pointer hover:bg-white/5 transition-all text-white/10 hover:text-[var(--color-primary)]">
                          <Plus size={40} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* CTA - Final Uplink */}
      {mockData.sectionVisibility.cta && (
      <section className="py-64 relative bg-[var(--color-background)] text-center px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-primary)]/5 to-transparent" />
          <div className="container mx-auto relative z-10 max-w-5xl">
              <h2 className="text-7xl md:text-[10rem] font-black italic tracking-tighter leading-none mb-16 uppercase shadow-2xl">
                 INITIALIZE <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)]">UPGRADE.</span>
              </h2>
              <div className="flex flex-col sm:flex-row justify-center gap-10">
                   <Button className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-black rounded-full h-24 px-16 font-black uppercase tracking-[0.3em] text-xs shadow-[0_0_60px_rgba(6,182,212,0.3)] hover:scale-105 transition-all">
                       Submit Data Node
                   </Button>
                   <Button variant="outline" className="border-white/10 text-white rounded-full h-24 px-16 font-black uppercase tracking-[0.3em] text-xs hover:bg-white/5 transition-all">
                       Download Protocols
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - The Uplink */}
      <footer id="contact" className="bg-[var(--color-background)] pt-40 pb-20 px-6 border-t border-white/5">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-40 items-start">
                  <div className="lg:col-span-5">
                      <div className="flex items-center gap-4 mb-12">
                           <div className="w-14 h-14 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-2xl flex items-center justify-center p-0.5">
                               <div className="w-full h-full bg-[var(--color-background)] rounded-[14px] flex items-center justify-center">
                                   <Rocket size={24} className="text-[var(--color-primary)]" />
                               </div>
                           </div>
                           <h2 className="text-4xl font-black italic uppercase tracking-tighter">{schoolName}.</h2>
                      </div>
                      <p className="text-lg font-light leading-relaxed mb-12 italic text-white/30 border-l border-white/10 pl-10 max-w-sm">"The future is not something we inhabit, but something we construct molecule by molecule."</p>
                      <div className="flex gap-4">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-14 h-14 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-black transition-all shadow-sm"><Icon size={24} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-white/20 mb-12">SYSTEMS</h4>
                      <ul className="space-y-6 text-sm font-black uppercase tracking-widest text-white/40">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-6"><span>0{i+1}</span> {item.label}</a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-3">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-white/20 mb-12">COORDINATES</h4>
                      <div className="space-y-12">
                          <div className="flex gap-8 group">
                              <MapPin size={28} className="text-white/10 shrink-0 group-hover:text-[var(--color-primary)] transition-colors" />
                              <p className="text-sm font-bold leading-tight text-white/60 uppercase tracking-widest">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="flex gap-8 group">
                              <Phone size={28} className="text-white/10 shrink-0 group-hover:text-[var(--color-primary)] transition-colors" />
                              <p className="text-sm font-bold text-white/60 tracking-[0.2em]">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>

                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-white/20 mb-12">UPLINK</h4>
                      <div className="space-y-8">
                           <p className="text-xs text-white/20 font-bold uppercase tracking-widest italic">Join the broadcast network.</p>
                           <div className="flex gap-2 border-b border-white/10 pb-4 hover:border-[var(--color-primary)] transition-all">
                                <input type="email" placeholder="NODE@NEBULA.AC" className="bg-transparent border-none focus:ring-0 text-sm font-bold w-full placeholder:text-white/10 text-[var(--color-primary)]" />
                                <button className="text-[var(--color-primary)] hover:scale-110 transition-transform"><Send size={24} /></button>
                           </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-20 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-black uppercase tracking-[0.5em] text-white/10">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // V2.9.0 // DARK_MATTER_ENCRYPTION</p>
                  <div className="flex gap-12 italic underline underline-offset-4">
                      <a href="#" className="hover:text-white">Protocol X</a>
                      <a href="#" className="hover:text-white">Interface Opts</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* Full UI Overlay Menu */}
      {menuOpen && (
          <div className="fixed inset-0 z-[1000] bg-[var(--color-background)] p-12 flex flex-col animate-in fade-in zoom-in duration-500 overflow-hidden">
               <div className="flex justify-between items-center mb-24 border-b border-white/10 pb-12">
                   <h2 className="text-5xl font-black italic tracking-tighter uppercase">{schoolName}</h2>
                   <button onClick={() => setMenuOpen(false)} className="text-white"><X size={64} /></button>
               </div>
               <nav className="flex flex-col gap-12">
                   {mockData.nav.filter(n => n.visible).map((n, i) => (
                       <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-7xl md:text-9xl font-black uppercase tracking-tighter italic text-white/10 hover:text-[var(--color-primary)] hover:translate-x-12 transition-all">
                           {n.label}
                       </a>
                   ))}
               </nav>
               <div className="mt-auto flex justify-between items-end border-t border-white/10 pt-12">
                    <p className="text-[10px] font-black uppercase tracking-[0.8em] text-white/10">SYSTEM_OVERRIDE_ACTIVE</p>
                    <div className="text-9xl font-black text-white/[0.02]">0101</div>
               </div>
          </div>
      )}
    </div>
  );
};

export default FutureScholar;
